import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Container,
  Alert,
  Button,
  Stack,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material'
import { useInvoiceAnalyser } from '@/hooks/useInvoiceAnalyser'
import { useDatabase } from '@/hooks/useDatabase'
import { DocumentDropzone } from '@/components/InvoiceAnalyser/Dropzone'
import { ProcessingStatus } from '@/components/InvoiceAnalyser/ProcessingStatus'
import { ExtractionResult } from '@/components/InvoiceAnalyser/ExtractionResult'
import { HistoryPanel } from '@/components/InvoiceAnalyser/HistoryPanel'
import { EvidenceDialog } from '@/components/InvoiceAnalyser/EvidenceDialog'
import { DuplicateWarning } from '@/components/InvoiceAnalyser/DuplicateWarning'
import type { ExtractionHistory, InvoiceExtraction } from '@/types/invoiceAnalyser'
import { loadSettings } from '@/utils/storage'

export default function InvoiceAnalyserPage() {
  const navigate = useNavigate()
  const [history, setHistory] = useState<ExtractionHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [currentResult, setCurrentResult] = useState<InvoiceExtraction | null>(null)
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null)
  const [evidenceOpen, setEvidenceOpen] = useState(false)
  const [showDropzone, setShowDropzone] = useState(true)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [pendingExtraction, setPendingExtraction] = useState<InvoiceExtraction | null>(null)
  const [duplicateInfo, setDuplicateInfo] = useState<{
    supplierName: string
    rechnungsnummer: string
    dateInvoice: string | null
    duplicateId: string | null
  } | null>(null)

  const {
    isUploading,
    currentJob,
    getHistory,
    uploadAndProcess,
    deleteDocument,
    stopPolling,
    resetCurrentJob,
    addSupplierToWhitelist,
    addRecipientToWhitelist,
  } = useInvoiceAnalyser()

  const { createInvoice, checkDuplicate } = useDatabase()

  // Refresh history
  const refreshHistory = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getHistory()
      setHistory(data)
    } catch (error) {
      console.error('Failed to load history:', error)
    } finally {
      setLoading(false)
    }
  }, [getHistory])

  // Load history on mount
  useEffect(() => {
    void refreshHistory()
  }, [refreshHistory])

  // Handle file selection and processing
  const handleFileSelect = useCallback(async (file: File) => {
    setShowDropzone(false)
    setCurrentResult(null)

    const settings = loadSettings()
    const ocrModel = settings.analyserOcrModels?.[0]
    const extractionModel = settings.analyserExtractionModels?.[0]

    try {
      // Upload and process in one go
      const { result, documentId } = await uploadAndProcess(
        file,
        {
          ocrModel,
          extractionModel,
        },
        () => {
          // Job update callback
        }
      )

      setCurrentResult(result)
      setCurrentDocumentId(documentId)
      setShowDropzone(true)
      await refreshHistory()
    } catch (error) {
      console.error('Processing failed:', error)
      setShowDropzone(true)
    }
  }, [uploadAndProcess, refreshHistory])

  // Handle viewing evidence
  const handleViewEvidence = useCallback(() => {
    setEvidenceOpen(true)
  }, [])

  // Handle history item click
  const handleHistoryItemClick = useCallback((item: ExtractionHistory) => {
    if (item.result) {
      setCurrentResult(item.result)
      setCurrentDocumentId(item.documentId)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Reset for new upload
  const handleNewUpload = useCallback(() => {
    stopPolling()
    resetCurrentJob()
    setCurrentResult(null)
    setCurrentDocumentId(null)
    setShowDropzone(true)
  }, [resetCurrentJob, stopPolling])

  // Handle delete history item
  const handleDeleteItem = useCallback(async (documentId: string) => {
    try {
      await deleteDocument(documentId)
      await refreshHistory()
      // Clear current result if it was the deleted item
      if (currentDocumentId === documentId) {
        setCurrentResult(null)
        setCurrentDocumentId(null)
        setShowDropzone(true)
      }
    } catch (error) {
      console.error('Failed to delete document:', error)
    }
  }, [currentDocumentId, deleteDocument, refreshHistory])

  // Handle adding supplier to whitelist
  const handleAddSupplier = useCallback(async (supplierName: string) => {
    const id = supplierName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    try {
      await addSupplierToWhitelist({
        id,
        names: [supplierName],
        normalizedName: supplierName,
        ustId: ''
      })
      setToastMessage(`"${supplierName}" added to supplier whitelist`)
      setTimeout(() => setToastMessage(null), 3000)
    } catch (error) {
      console.error('Failed to add supplier:', error)
      const errorMsg = error instanceof Error ? error.message : 'Failed to add supplier'
      setToastMessage(`Error: ${errorMsg}`)
      setTimeout(() => setToastMessage(null), 5000)
    }
  }, [addSupplierToWhitelist])

  // Handle adding recipient to whitelist
  const handleAddRecipient = useCallback(async (recipientName: string) => {
    const id = recipientName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    try {
      await addRecipientToWhitelist({
        id,
        names: [recipientName],
        normalizedName: recipientName,
        ustId: ''
      })
      setToastMessage(`"${recipientName}" added to recipient whitelist`)
      setTimeout(() => setToastMessage(null), 3000)
    } catch (error) {
      console.error('Failed to add recipient:', error)
      const errorMsg = error instanceof Error ? error.message : 'Failed to add recipient'
      setToastMessage(`Error: ${errorMsg}`)
      setTimeout(() => setToastMessage(null), 5000)
    }
  }, [addRecipientToWhitelist])

  // Handle adding to database
  const handleAddToDatabase = useCallback(async (extraction: InvoiceExtraction) => {
    const supplierName = extraction.supplier || ''
    const rechnungsnummer = extraction.invoice_number || ''
    const dateInvoice = extraction.invoice_date || null

    // Check for duplicates first
    try {
      const dupResult = await checkDuplicate({
        supplierName,
        rechnungsnummer,
      })

      if (dupResult.isDuplicate) {
        // Store pending extraction and show warning dialog
        setPendingExtraction(extraction)
        setDuplicateInfo({
          supplierName,
          rechnungsnummer,
          dateInvoice,
          duplicateId: dupResult.duplicateIds[0] || null,
        })
        setDuplicateOpen(true)
        return
      }
    } catch (error) {
      console.error('Duplicate check failed:', error)
      // Continue with creation on error
    }

    // Actually create the invoice
    await actuallyCreateInvoice(extraction)
  }, [checkDuplicate, currentDocumentId])

  // Separate function for actual invoice creation
  const actuallyCreateInvoice = useCallback(async (extraction: InvoiceExtraction) => {
    try {
      await createInvoice({
        supplierName: extraction.supplier || '',
        dateInvoice: extraction.invoice_date || null,
        brutto: extraction.total_gross ?? null,
        netto: extraction.total_net ?? null,
        rechnungsnummer: extraction.invoice_number || '',
        comment: '',
        currency: extraction.currency || null,
        projectName: extraction.project_name || null,
        recipient: extraction.recipient || null,
        ustId: extraction.ust_id || null,
        confidence: extraction.confidence,
        source: 'analyser',
        vatAmount: extraction.vat_amount ?? null,
        taxFreeAmount: extraction.tax_free_amount ?? null,
        taxableNetAmount: extraction.taxable_net_amount ?? null,
        vatBreakdown: extraction.vat_breakdown ?? null,
        taxFreeBreakdown: extraction.tax_free_breakdown ?? null,
        sourceDocumentId: currentDocumentId,
        poId: extraction.po_id ?? null,
        customFields: {},
      })
      setToastMessage('Added to database successfully')
      setTimeout(() => setToastMessage(null), 3000)
    } catch (error) {
      console.error('Failed to add to database:', error)
      const errorMsg = error instanceof Error ? error.message : 'Failed to add to database'
      setToastMessage(`Error: ${errorMsg}`)
      setTimeout(() => setToastMessage(null), 5000)
    }
  }, [createInvoice, currentDocumentId])

  // Handle duplicate warning confirm
  const handleDuplicateConfirm = useCallback(() => {
    setDuplicateOpen(false)
    if (pendingExtraction) {
      void actuallyCreateInvoice(pendingExtraction)
      setPendingExtraction(null)
    }
  }, [pendingExtraction, actuallyCreateInvoice])

  // Handle duplicate warning cancel
  const handleDuplicateCancel = useCallback(() => {
    setDuplicateOpen(false)
    setPendingExtraction(null)
    setDuplicateInfo(null)
  }, [])

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">
          Invoice Analyser
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/')}
            variant="outlined"
            size="small"
          >
            Dashboard
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={refreshHistory}
            disabled={loading}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Upload invoices (PDF, images, ZUGFeRD XML) to extract structured data using AI.
        Configure models in Settings.
      </Alert>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        {/* Main content area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Dropzone - show when ready */}
          {showDropzone && !currentJob && !currentResult && (
            <DocumentDropzone
              onFileSelect={handleFileSelect}
              isUploading={isUploading}
            />
          )}

          {/* Processing status */}
          {currentJob && currentJob.status === 'processing' && (
            <ProcessingStatus job={currentJob} />
          )}

          {/* Extraction result */}
          {currentResult && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">Latest Result</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleNewUpload}
                  variant="outlined"
                  size="small"
                >
                  Upload New
                </Button>
              </Stack>
              <ExtractionResult
                result={currentResult}
                onViewEvidence={handleViewEvidence}
                onAddSupplierToWhitelist={handleAddSupplier}
                onAddRecipientToWhitelist={handleAddRecipient}
                onAddToDatabase={handleAddToDatabase}
              />
            </Box>
          )}

          {/* Failed job */}
          {currentJob && currentJob.status === 'failed' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={handleNewUpload}>
                  Try Again
                </Button>
              }
            >
              {currentJob.error || 'Processing failed. Please try again.'}
            </Alert>
          )}
        </Box>

        {/* Sidebar - History */}
        <Box>
          <HistoryPanel
            history={history}
            onItemClick={handleHistoryItemClick}
            onDeleteItem={handleDeleteItem}
          />
        </Box>
      </Box>

      {/* Evidence Dialog */}
      <EvidenceDialog
        open={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        extraction={currentResult}
      />

      {/* Duplicate Warning Dialog */}
      {duplicateInfo && (
        <DuplicateWarning
          open={duplicateOpen}
          supplierName={duplicateInfo.supplierName}
          rechnungsnummer={duplicateInfo.rechnungsnummer}
          dateInvoice={duplicateInfo.dateInvoice}
          duplicateId={duplicateInfo.duplicateId}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
        />
      )}

      {/* Toast notification */}
      {toastMessage && (
        <Alert
          severity={toastMessage.startsWith('Error') ? 'error' : 'success'}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            minWidth: 300,
          }}
          onClose={() => setToastMessage(null)}
        >
          {toastMessage}
        </Alert>
      )}
    </Container>
  )
}
